import { config } from "../models/config";
import { jsonHeaders } from "./apiHeaders";
import type {
  Account,
  AddProjectMemberRequest,
  ChangeTierRequest,
  CompleteOnboardingRequest,
  CompanyProfile,
  CreateInvitationRequest,
  CreateProjectRequest,
  CreateWorkspaceRequest,
  IndividualProfile,
  Project,
  ProjectMember,
  ProjectRole,
  Subscription,
  SubscriptionEvent,
  UpdateAccountRequest,
  UpdateProjectRequest,
  UpdateWorkspaceRequest,
  Workspace,
  WorkspaceInvitation,
  WorkspaceMember,
  WorkspaceRole,
  UserView,
} from "../models/account";
import type { AiUsage, UpdateAiSettingsRequest, WorkspaceAiSettings } from "../models/ai";

export const lookupUsers = (getToken: GetToken, ids: string[]) =>
  call<UserView[]>(getToken, "POST", "/api/users/lookup", { ids });

// Typed client for the account/tenancy REST surface. Plain async functions (no React), following
// the same bearer-token pattern as apiClient.ts's authedFetch — each call reads the freshest token
// via the getToken accessor supplied by AuthProvider/useShellController.
export type GetToken = () => string | null;

/** Structured API error carrying the BFF's {code,category,status,message,...} error body. */
export class ApiError extends Error {
  code?: string;
  category?: string;
  status: number;
  details?: unknown;

  constructor(status: number, body: unknown) {
    const b = (body ?? {}) as Record<string, unknown>;
    super(typeof b.message === "string" ? b.message : `요청이 실패했습니다 (${status})`);
    this.status = status;
    this.code = typeof b.code === "string" ? b.code : undefined;
    this.category = typeof b.category === "string" ? b.category : undefined;
    this.details = b.details;
  }

  /** True when this error is a plan-limit rejection that should show an upgrade hint. */
  get isPlanLimit(): boolean {
    return (
      this.category === "FORBIDDEN" &&
      (this.code === "WS-0002" || this.code === "PROJ-0002")
    );
  }
}

/** Maps an unknown error (ideally an ApiError) to a Korean, user-facing message. */
/**
 * Domain invariants the shell cannot always prevent, in Korean.
 *
 * The aggregates in `dev.compath.domain.model` guard themselves with English sentences — correct for
 * domain code, which has no business carrying UI copy — and those sentences travelled all the way to
 * a Korean screen. Every entry here is a state the user can reach by RACING the UI: acting on a review
 * task someone else just answered, releasing a document a colleague obsoleted, starting a sprint that
 * has since begun. Where the shell can see the problem first it says so before the click instead (see
 * RequestReviewForm's `blocked`), which is always the better fix; this is the net under it.
 */
const DOMAIN_MESSAGE_KO: Record<string, string> = {
  "a document needs a revision before review":
    "이 문서에는 아직 리비전이 없습니다. 먼저 리비전을 업로드한 뒤 검토를 요청하세요.",
  "only a DRAFT document can enter review": "초안 상태의 문서만 검토를 요청할 수 있습니다.",
  "only a DRAFT document can be edited": "초안 상태의 문서만 수정할 수 있습니다.",
  "only an APPROVED document can be released": "승인된 문서만 릴리스할 수 있습니다.",
  "only a RELEASED or APPROVED document can start a new revision":
    "릴리스되었거나 승인된 문서만 새 리비전을 시작할 수 있습니다.",
  "document is already obsolete": "이미 폐기된 문서입니다.",
  "this review task has already been acted on": "이미 처리된 검토 작업입니다. 화면을 새로고침해 주세요.",
  "only the assigned reviewer/approver may act on this task":
    "지정된 검토자·승인자만 이 작업을 처리할 수 있습니다.",
  "action must be APPROVED or REJECTED": "승인 또는 반려만 선택할 수 있습니다.",
  "a trace link cannot connect an endpoint to itself": "같은 대상끼리는 추적성 링크를 연결할 수 없습니다.",
  "only a FUTURE sprint can be started": "시작 전 스프린트만 시작할 수 있습니다.",
  "only an ACTIVE sprint can be completed": "진행 중인 스프린트만 완료할 수 있습니다.",
  "only a PLANNED version can enter development": "계획 상태의 버전만 개발을 시작할 수 있습니다.",
  "only a RELEASED version can be superseded": "릴리스된 버전만 대체할 수 있습니다.",
  "account type cannot change after onboarding": "온보딩 이후에는 계정 유형을 바꿀 수 없습니다.",
};

export function friendlyMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.isPlanLimit) {
      return "현재 플랜의 한도에 도달했습니다. SPECIAL 플랜으로 업그레이드해주세요.";
    }
    // Unmapped messages are returned as-is on purpose: inventing a vague Korean sentence would hide
    // what actually went wrong, and the server's own validation copy is already Korean.
    return DOMAIN_MESSAGE_KO[err.message] ?? err.message;
  }
  if (err instanceof Error) return err.message;
  return String(err);
}

async function call<T>(
  getToken: GetToken,
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(`${config.apiBase}${path}`, {
    method,
    headers: jsonHeaders(getToken),
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new ApiError(res.status, detail);
  }
  if (res.status === 204) return undefined as T;
  return res.json().catch(() => undefined as T);
}

// ---- Account ----
export const getMe = (getToken: GetToken) => call<Account>(getToken, "GET", "/api/account/me");

export const updateMe = (getToken: GetToken, req: UpdateAccountRequest) =>
  call<Account>(getToken, "PATCH", "/api/account/me", req);

export const updateIndividualProfile = (getToken: GetToken, req: IndividualProfile) =>
  call<Account>(getToken, "PUT", "/api/account/me/profile/individual", req);

export const updateCompanyProfile = (getToken: GetToken, req: CompanyProfile) =>
  call<Account>(getToken, "PUT", "/api/account/me/profile/company", req);

export const agreeTerms = (getToken: GetToken) =>
  call<Account>(getToken, "POST", "/api/account/me/agree-terms");

export const completeOnboarding = (getToken: GetToken, req: CompleteOnboardingRequest) =>
  call<Account>(getToken, "POST", "/api/account/me/onboarding", req);

// ---- Admin: user management (group:sp-admin / admin) ----
export const adminListUsers = (getToken: GetToken) =>
  call<Account[]>(getToken, "GET", "/api/admin/accounts");

export const adminGetUser = (getToken: GetToken, id: string) =>
  call<Account>(getToken, "GET", `/api/admin/accounts/${id}`);

export const adminUpdateUser = (getToken: GetToken, id: string, req: UpdateAccountRequest) =>
  call<Account>(getToken, "PATCH", `/api/admin/accounts/${id}`, req);

export const adminDeactivateUser = (getToken: GetToken, id: string) =>
  call<Account>(getToken, "POST", `/api/admin/accounts/${id}/deactivate`);

export const adminReactivateUser = (getToken: GetToken, id: string) =>
  call<Account>(getToken, "POST", `/api/admin/accounts/${id}/reactivate`);

export const adminDeleteUser = (getToken: GetToken, id: string) =>
  call<Account>(getToken, "DELETE", `/api/admin/accounts/${id}`);

// ---- Subscription ----
export const getSubscription = (getToken: GetToken) =>
  call<Subscription>(getToken, "GET", "/api/account/subscription");

export const getSubscriptionHistory = (getToken: GetToken) =>
  call<SubscriptionEvent[]>(getToken, "GET", "/api/account/subscription/history");

export const changeTier = (getToken: GetToken, req: ChangeTierRequest) =>
  call<Subscription>(getToken, "POST", "/api/account/subscription/change-tier", req);

export const cancelSubscription = (getToken: GetToken) =>
  call<Subscription>(getToken, "POST", "/api/account/subscription/cancel");

// ---- Workspaces ----
export const listWorkspaces = (getToken: GetToken) =>
  call<Workspace[]>(getToken, "GET", "/api/workspaces");

export const createWorkspace = (getToken: GetToken, req: CreateWorkspaceRequest) =>
  call<Workspace>(getToken, "POST", "/api/workspaces", req);

export const getWorkspace = (getToken: GetToken, id: string) =>
  call<Workspace>(getToken, "GET", `/api/workspaces/${id}`);

export const updateWorkspace = (getToken: GetToken, id: string, req: UpdateWorkspaceRequest) =>
  call<Workspace>(getToken, "PATCH", `/api/workspaces/${id}`, req);

export const archiveWorkspace = (getToken: GetToken, id: string) =>
  call<Workspace>(getToken, "POST", `/api/workspaces/${id}/archive`);

/** Permanent, owner-only, and takes the workspace's projects/documents/files with it. */
export const deleteWorkspace = (getToken: GetToken, id: string) =>
  call<void>(getToken, "DELETE", `/api/workspaces/${id}`);

// ---- Workspace AI settings + usage ----
export const getAiSettings = (getToken: GetToken, workspaceId: string) =>
  call<WorkspaceAiSettings>(getToken, "GET", `/api/workspaces/${workspaceId}/ai-settings`);
export const updateAiSettings = (getToken: GetToken, workspaceId: string, req: UpdateAiSettingsRequest) =>
  call<WorkspaceAiSettings>(getToken, "PATCH", `/api/workspaces/${workspaceId}/ai-settings`, req);
export const getAiUsage = (getToken: GetToken, workspaceId: string, period?: string) =>
  call<AiUsage>(getToken, "GET", `/api/workspaces/${workspaceId}/ai-usage${period ? `?period=${period}` : ""}`);

export const listWorkspaceMembers = (getToken: GetToken, id: string) =>
  call<WorkspaceMember[]>(getToken, "GET", `/api/workspaces/${id}/members`);

export const updateWorkspaceMemberRole = (
  getToken: GetToken,
  id: string,
  userId: string,
  role: WorkspaceRole,
) => call<WorkspaceMember>(getToken, "PATCH", `/api/workspaces/${id}/members/${userId}`, { role });

export const removeWorkspaceMember = (getToken: GetToken, id: string, userId: string) =>
  call<void>(getToken, "DELETE", `/api/workspaces/${id}/members/${userId}`);

export const listWorkspaceInvitations = (getToken: GetToken, id: string) =>
  call<WorkspaceInvitation[]>(getToken, "GET", `/api/workspaces/${id}/invitations`);

export const createWorkspaceInvitation = (
  getToken: GetToken,
  id: string,
  req: CreateInvitationRequest,
) => call<WorkspaceInvitation>(getToken, "POST", `/api/workspaces/${id}/invitations`, req);

export const revokeWorkspaceInvitation = (
  getToken: GetToken,
  id: string,
  invitationId: string,
) => call<void>(getToken, "DELETE", `/api/workspaces/${id}/invitations/${invitationId}`);

// ---- My invitations ----
export const listMyInvitations = (getToken: GetToken) =>
  call<WorkspaceInvitation[]>(getToken, "GET", "/api/invitations");

export const acceptInvitation = (getToken: GetToken, id: string) =>
  call<void>(getToken, "POST", `/api/invitations/${id}/accept`);

export const declineInvitation = (getToken: GetToken, id: string) =>
  call<void>(getToken, "POST", `/api/invitations/${id}/decline`);

// ---- Projects ----
export const listProjects = (getToken: GetToken, workspaceId: string) =>
  call<Project[]>(getToken, "GET", `/api/workspaces/${workspaceId}/projects`);

export const createProject = (getToken: GetToken, workspaceId: string, req: CreateProjectRequest) =>
  call<Project>(getToken, "POST", `/api/workspaces/${workspaceId}/projects`, req);

export const getProject = (getToken: GetToken, id: string) =>
  call<Project>(getToken, "GET", `/api/projects/${id}`);

export const updateProject = (getToken: GetToken, id: string, req: UpdateProjectRequest) =>
  call<Project>(getToken, "PATCH", `/api/projects/${id}`, req);

export const archiveProject = (getToken: GetToken, id: string) =>
  call<Project>(getToken, "POST", `/api/projects/${id}/archive`);

export const listProjectMembers = (getToken: GetToken, id: string) =>
  call<ProjectMember[]>(getToken, "GET", `/api/projects/${id}/members`);

export const addProjectMember = (getToken: GetToken, id: string, req: AddProjectMemberRequest) =>
  call<ProjectMember>(getToken, "POST", `/api/projects/${id}/members`, req);

export const updateProjectMemberRole = (
  getToken: GetToken,
  id: string,
  userId: string,
  role: ProjectRole,
) => call<ProjectMember>(getToken, "PATCH", `/api/projects/${id}/members/${userId}`, { role });

export const removeProjectMember = (getToken: GetToken, id: string, userId: string) =>
  call<void>(getToken, "DELETE", `/api/projects/${id}/members/${userId}`);
